import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { IconComponent, IconName } from '../../../components/icon/icon.component';
import { ObjectUsages, UsageLine, UsageOwner, UsageStep } from '../../../models/object-mapping.model';

/** Alter und neuer Name fuer die Vorschau des Umbenennens. */
export interface RenamePair {
  oldName: string;
  newName: string;
}

/** Eine Stelle: ein Testfall, Testmodul oder freier Schritt mit einer Zeile (Bundle `verwender`). */
interface Place {
  key: string;
  title: string;
  /** `null` fuer freie Schritte; sie behalten ihr bisheriges Symbol. */
  icon: IconName | null;
  stepKey: string | null;
  line: UsageLine | null;
  link: string[];
}

/**
 * Verwender eines Objekts oder Testelements als flache Liste je Stelle (NEX-1278, NEX-2451), Design
 * `page-objektbibliothek.jsx:467-480`. Mit `rename` zeigt sie die Vorschau des Umbenennens (`:155-168`).
 */
@Component({
  selector: 'app-object-usage-list',
  imports: [RouterLink, TranslatePipe, MatIcon, IconComponent],
  templateUrl: './object-usage-list.component.html',
  styleUrl: './object-usage-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectUsageListComponent {
  readonly usages = input.required<ObjectUsages>();
  readonly projectId = input.required<string>();
  readonly rename = input<RenamePair | null>(null);
  /** Ein Klick auf eine Stelle oeffnet sie; der Dialog darum schliesst sich. */
  readonly opened = output<void>();

  readonly places = computed<Place[]>(() => {
    const usages = this.usages();
    const project = ['/projects', this.projectId()];
    return [
      ...usages.testCases.flatMap((owner) => this.ownerPlaces(owner, project)),
      ...usages.modules.flatMap((owner) => this.ownerPlaces(owner, project)),
      ...usages.unlinkedSteps.flatMap((step) =>
        linesOf(step).map((line) => ({
          key: step.stepKey,
          title: step.stepTitle,
          icon: null,
          stepKey: step.stepKey,
          line,
          link: [...project, 'test-steps', step.stepId, 'edit'],
        })),
      ),
    ];
  });

  readonly previewHead = computed(() => {
    const count = this.places().length;
    if (count === 0) return { key: 'objectMappings.rename.placesNone', count };
    return { key: count === 1 ? 'objectMappings.rename.placesOne' : 'objectMappings.rename.placesOther', count };
  });

  readonly duplicatesHint = computed(() => {
    const duplicates = this.usages().duplicates;
    if (!duplicates) {
      return null;
    }
    const kind = duplicates.assigned ? 'assigned' : duplicates.resolvedMapping ? 'notAssigned' : 'unresolved';
    return {
      key: `objectMappings.usages.duplicates.${kind}`,
      params: { others: duplicates.otherMappings.join(', '), resolved: duplicates.resolvedMapping },
    };
  });

  private ownerPlaces(owner: UsageOwner, project: string[]): Place[] {
    const module = owner.ownerKind === 'MODULE';
    const link = [...project, module ? 'test-modules' : 'test-cases', owner.id, 'edit'];
    const place = (step: UsageStep | null, line: UsageLine | null): Place => ({
      key: owner.key,
      title: owner.title,
      icon: module ? 'puzzle' : 'folder',
      stepKey: step?.stepKey ?? null,
      line,
      link,
    });
    const places = owner.steps.flatMap((step) => linesOf(step).map((line) => place(step, line)));
    return places.length > 0 ? places : [place(null, null)];
  }
}

/** Ein Schritt ohne Zeilen zaehlt trotzdem als eine Stelle. */
function linesOf(step: UsageStep): (UsageLine | null)[] {
  return step.lines.length > 0 ? step.lines : [null];
}
